import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete,
  Grid, Chip, Alert, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Snackbar, Card, CardContent,
} from '@mui/material';
import {
  Spa, CardMembership, Search, CheckCircle, AccessTime, Block, Add
} from '@mui/icons-material';
import { patientAPI, servicePackagesAPI } from '../services/api';

function ServicePackages() {
  const [tab, setTab] = useState(0);

  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [patientPackages, setPatientPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amountPaid, setAmountPaid] = useState('');
  const [purchaseNotes, setPurchaseNotes] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const normalizePatient = (p) => ({
    id: p.id,
    first_name: p.first_name || p.firstName || '',
    last_name: p.last_name || p.lastName || '',
    patient_code: p.patient_code || p.patientCode || '',
    phone: p.phone || ''
  });

  useEffect(() => {
    setPlansLoading(true);
    servicePackagesAPI.getPlans()
      .then(res => setPlans(res.data.data || []))
      .catch(() => {})
      .finally(() => setPlansLoading(false));

    patientAPI.getAll({ limit: 50 })
      .then(res => setPatientOptions((res.data.data || []).map(normalizePatient)))
      .catch(() => {});
  }, []);

  const handlePatientSearch = useCallback(async (query) => {
    if (query.length < 2) {
      patientAPI.getAll({ limit: 50 })
        .then(res => setPatientOptions((res.data.data || []).map(normalizePatient)))
        .catch(() => {});
      return;
    }
    try {
      const res = await patientAPI.search(query);
      setPatientOptions((res.data.data || []).map(normalizePatient));
    } catch {}
  }, []);

  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setPatientPackages([]);
    if (!patient) return;
    setPackagesLoading(true);
    try {
      const res = await servicePackagesAPI.getPatientPackages(patient.id);
      setPatientPackages(res.data.data || []);
    } catch {
      showSnackbar('Failed to load patient packages', 'error');
    } finally {
      setPackagesLoading(false);
    }
  };

  const openPurchaseDialog = (plan) => {
    setSelectedPlan(plan);
    setAmountPaid(plan.amount.toString());
    setPurchaseNotes('');
    setPaymentMethod('cash');
    setPurchaseDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!selectedPatient) { showSnackbar('Please select a patient first', 'error'); return; }
    if (!amountPaid || parseFloat(amountPaid) <= 0) { showSnackbar('Please enter a valid amount', 'error'); return; }
    setPurchaseLoading(true);
    try {
      await servicePackagesAPI.purchasePackage({
        patientId: selectedPatient.id,
        planId: selectedPlan.id,
        amountPaid: parseFloat(amountPaid),
        notes: purchaseNotes || null
      });
      showSnackbar(`${selectedPlan.name} purchased for ${selectedPatient.first_name}!`, 'success');
      setPurchaseDialogOpen(false);
      if (tab === 1 && selectedPatient) {
        const res = await servicePackagesAPI.getPatientPackages(selectedPatient.id);
        setPatientPackages(res.data.data || []);
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Purchase failed', 'error');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const statusColor = (s) => s === 'active' ? 'success' : s === 'exhausted' ? 'warning' : 'default';
  const statusIcon = (s) => s === 'active' ? <CheckCircle fontSize="small" /> : s === 'expired' ? <Block fontSize="small" /> : <AccessTime fontSize="small" />;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
        <CardMembership color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight="bold">Service Packages</Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Browse & Purchase" icon={<Add />} iconPosition="start" />
        <Tab label="Patient Packages" icon={<CardMembership />} iconPosition="start" />
      </Tabs>

      {/* Shared patient selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Select Patient</Typography>
        <Autocomplete
          options={patientOptions}
          getOptionLabel={opt => `${opt.first_name} ${opt.last_name} (${opt.patient_code}) — ${opt.phone}`}
          onInputChange={(_, val) => handlePatientSearch(val)}
          onChange={(_, val) => handleSelectPatient(val)}
          renderInput={(params) => (
            <TextField {...params} label="Search Patient" placeholder="Name or patient code"
              InputProps={{ ...params.InputProps, startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          )}
        />
        {selectedPatient && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Selected: <strong>{selectedPatient.first_name} {selectedPatient.last_name}</strong> ({selectedPatient.patient_code})
          </Alert>
        )}
      </Paper>

      {/* BROWSE PLANS TAB */}
      {tab === 0 && (
        plansLoading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
          <Box>
            {!selectedPatient && (
              <Alert severity="info" sx={{ mb: 2 }}>Select a patient above to enable package purchase.</Alert>
            )}
            <Grid container spacing={3}>
              {plans.map(plan => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <Card variant="outlined" sx={{ borderColor: 'primary.light', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">{plan.name}</Typography>
                      <Typography variant="h5" color="primary.main" gutterBottom>
                        ₹{parseFloat(plan.amount).toLocaleString('en-IN')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        <Chip label={`${plan.sessions_total} Free Sessions`} color="primary" size="small" icon={<Spa />} />
                        <Chip label={`Valid ${plan.validity_months} month${plan.validity_months > 1 ? 's' : ''}`} size="small" icon={<AccessTime />} />
                        {plan.service_type && <Chip label={plan.service_type} size="small" variant="outlined" />}
                      </Box>
                      {plan.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {plan.description}
                        </Typography>
                      )}
                      {plan.treatments?.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight="bold">Included:</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {plan.treatments.filter(t => t.is_deductible).map(t => (
                              <Chip key={t.treatment_id} label={t.name} size="small" variant="outlined" color="primary" />
                            ))}
                            {plan.treatments.filter(t => !t.is_deductible).map(t => (
                              <Chip key={t.treatment_id} label={`${t.name} ∞`} size="small" variant="outlined" color="success"
                                title="Always free, not counted" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth variant="contained"
                        onClick={() => openPurchaseDialog(plan)}
                        disabled={!selectedPatient}
                      >
                        Purchase
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
              {plans.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">No active packages found. Add packages from the Admin Dashboard.</Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        )
      )}

      {/* PATIENT PACKAGES TAB */}
      {tab === 1 && (
        <Box>
          {!selectedPatient && <Alert severity="info">Select a patient above to view their packages.</Alert>}
          {selectedPatient && packagesLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />}
          {selectedPatient && !packagesLoading && patientPackages.length === 0 && (
            <Alert severity="info">No packages found for this patient.</Alert>
          )}
          {selectedPatient && !packagesLoading && patientPackages.length > 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Package</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Purchase Date</strong></TableCell>
                    <TableCell><strong>Expiry Date</strong></TableCell>
                    <TableCell align="center"><strong>Sessions Used / Total</strong></TableCell>
                    <TableCell align="center"><strong>Sessions Left</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Amount Paid</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patientPackages.map(pkg => (
                    <TableRow key={pkg.id} hover sx={{ opacity: pkg.status === 'active' ? 1 : 0.6 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={pkg.status === 'active' ? 'bold' : 'normal'}>
                          {pkg.plan_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={pkg.service_type?.toUpperCase() || 'PACKAGE'} size="small"
                          color={pkg.service_type === 'spa' ? 'primary' : pkg.service_type === 'salon' ? 'secondary' : 'default'}
                          icon={<Spa />}
                        />
                      </TableCell>
                      <TableCell>{new Date(pkg.purchase_date).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={pkg.status === 'expired' ? 'error' : 'inherit'}>
                          {new Date(pkg.expiry_date).toLocaleDateString('en-IN')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{pkg.sessions_used} / {pkg.sessions_total}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={pkg.sessions_remaining}
                          size="small"
                          color={pkg.sessions_remaining > 0 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={pkg.status.toUpperCase()} size="small"
                          color={statusColor(pkg.status)} icon={statusIcon(pkg.status)} />
                      </TableCell>
                      <TableCell align="right">₹{parseFloat(pkg.amount_paid).toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* PURCHASE DIALOG */}
      <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Purchase — {selectedPlan?.name}</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Alert severity="info">
                Patient gets <strong>{selectedPlan.sessions_total} free sessions</strong>{' '}
                valid for <strong>{selectedPlan.validity_months} month{selectedPlan.validity_months > 1 ? 's' : ''}</strong>.
                {selectedPlan.non_deductible_treatment_ids?.length > 0 &&
                  ` Plus ${selectedPlan.non_deductible_treatment_ids.length} always-free service(s) (never counted).`
                }
              </Alert>
              <Typography variant="body2">
                Patient: <strong>{selectedPatient?.first_name} {selectedPatient?.last_name}</strong> ({selectedPatient?.patient_code})
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Amount Paid (₹)" type="number"
                    value={amountPaid} onChange={e => setAmountPaid(e.target.value)}
                    helperText={`Package price: ₹${parseFloat(selectedPlan.amount).toLocaleString('en-IN')}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} label="Payment Method">
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="upi">UPI</MenuItem>
                      <MenuItem value="online">Online Transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Notes (optional)" value={purchaseNotes}
                    onChange={e => setPurchaseNotes(e.target.value)} multiline rows={2} />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePurchase} disabled={purchaseLoading}
            startIcon={purchaseLoading ? <CircularProgress size={16} color="inherit" /> : <CardMembership />}>
            Confirm Purchase
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ServicePackages;
